
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (csv: string) => void;
  isImporting: boolean;
}

const ImportDialog: React.FC<ImportDialogProps> = ({
  open,
  onOpenChange,
  onImport,
  isImporting,
}) => {
  const [csvContent, setCsvContent] = useState("");

  const handleImport = () => {
    if (!csvContent.trim()) return;
    onImport(csvContent);
  };

  const sampleCsv = `source_path,target_path,status_code,is_active,notes
/old-post-2024,/new-post-2025,301,true,Moved permanently
/outdated-page,/updated-page,301,true,Updated content location`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Import Redirects from CSV</DialogTitle>
          <DialogDescription>
            Paste your CSV data below. The CSV must include source_path and
            target_path columns.
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="text-sm">
              <p className="mb-2">
                Required columns: <code>source_path</code>,{" "}
                <code>target_path</code>
              </p>
              <p className="mb-2">
                Optional columns: <code>status_code</code>,{" "}
                <code>is_active</code>, <code>notes</code>
              </p>
              <div className="mt-2">
                <p className="font-semibold mb-1 text-xs">Sample CSV:</p>
                <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                  {sampleCsv}
                </pre>
              </div>
            </div>
          </AlertDescription>
        </Alert>

        <Textarea
          placeholder="Paste CSV content here..."
          value={csvContent}
          onChange={(e) => setCsvContent(e.target.value)}
          rows={8}
          className="font-mono text-sm"
        />

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!csvContent.trim() || isImporting}>
            {isImporting ? "Importing..." : "Import Redirects"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
